<template>
    <div class="page-root">

        <HeaderV2
            :isUserLoggedIn="isUserLoggedIn"
            :isGoldUser="isGoldUser"
            :routePrefix="routePrefix"
            :requiresAgeVerification="requiresAgeVerification"
            :promoteFreeJoin="false"
            @joinClick="isProducerJoinModalOpen = true"
            @goldJoinClick="isBuyStarsModalOpen = true"
        />

        <!-- Sticky wizard header -->
        <div v-if="!submitted" class="page-header">
            <ProgressBar :current-step="currentStep" :can-advance="canAdvance" @prev="handleBack" @next="nextStep" />
        </div>

        <!-- Step viewport -->
        <div class="page-body">
            <!-- Success State -->
            <div v-if="submitted" class="success-panel">
                <div class="success-icon-stack">
                    <div class="success-icon-wrap">
                        <Clapperboard class="icon-lg" style="color:#c4b5fd" />
                    </div>
                    <div class="success-icon-glow" />
                </div>
                <div style="text-align:center">
                    <h2 class="success-title">Pitch Submitted!</h2>
                    <p class="success-sub">
                        Your pitch is under review. Our team will reach out to the requested performers and assess feasibility.
                    </p>
                </div>
                <div class="success-steps">
                    <div v-for="(item, i) in successSteps" :key="i" class="success-step-row">
                        <span class="success-num">{{ i + 1 }}</span>
                        <p class="success-step-text">{{ item }}</p>
                    </div>
                </div>
                <button
                    @click="router.visit(route(routePrefix + '.studio.casting.view', { slug: submittedSlug }))"
                    class="primary-cta"
                >
                    View My Pitch
                </button>
            </div>

            <!-- Wizard Steps -->
            <Transition v-else :name="transitionName" mode="out-in" style="height:100%;display:flex;flex-direction:column;">
                <component
                    :is="currentStepComponent"
                    :key="currentStep"
                    v-bind="stepProps"
                    v-on="stepEvents"
                    style="height:100%"
                />
            </Transition>
        </div>

        <ProducerJoinModal :isOpen="isProducerJoinModalOpen"
                           :context="'pitch'"
                           :performer="castPerformer"
                           :source="joinModalSource"
                           @close="isProducerJoinModalOpen = false"
        />
        <CastingUpgradeModal
            :isOpen="isUpgradeModalOpen"
            source="Create_Upgrade_Modal_V3"
            source2="Create"
            @close="handleUpgradeModalClose"
        />
        <BuyStarsModal
            :isOpen="isBuyStarsModalOpen"
            :userBalance="userBalance"
            :starPacks="props.starPacks"
            @close="isBuyStarsModalOpen = false"
            @buy="handleBuyStarPack"
        />
    </div>
</template>

<script setup>
import CastStepVue     from '@/Components/Casting/Wizard/CastStep.vue'
import FormatStepVue   from '@/Components/Casting/Wizard/FormatStep.vue'
import SettingStepVue  from '@/Components/Casting/Wizard/SettingStep.vue'
import TitleStepVue    from '@/Components/Casting/Wizard/TitleStep.vue'
import SynopsisStepVue from '@/Components/Casting/Wizard/SynopsisStep.vue'
import LaunchStepVue   from '@/Components/Casting/Wizard/LaunchStep.vue'
import ProgressBar     from '@/Components/Casting/Wizard/ProgressBar.vue'
import HeaderV2        from '@/Components/Reels/HeaderV2.vue'
import {ref, computed, watch, onMounted, onUnmounted, nextTick} from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { Clapperboard } from 'lucide-vue-next'
import CastingUpgradeModal from '@/Components/Casting/CastingUpgradeModal.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import {trackEvent, trackFathomEvent, trackFeatureEvent} from '@/Helpers/analytics'
import { openStarPackPurchaseUrl } from '@/Helpers/starPackPurchase'
import { useRecordSgaGoal } from '@/composables/useRecordSgaGoal'
import ProducerJoinModal from "@/Components/Common/ProducerJoinModal.vue";

// ─── Inertia page props ───────────────────────────────────────────────────────
const props = defineProps({
    performers:      { type: Array,   default: () => [] },
    popularPerformers: { type: Array, default: () => [] },
    castingStats:    { type: Object,  default: () => ({ total_pitches: 0, cast_this_year: 0 }) },
    tags:            { type: Array,   default: () => [] },
    starPacks:       { type: Array,  required: true },
    userPitchesCount: { type: Number, default: 0 },
    source:          { type: String,  default: 'create_scene_v3' },
})

const { performers, popularPerformers, castingStats, tags, userPitchesCount } = props

// ─── Shared page props ────────────────────────────────────────────────────────
const { props: sharedProps } = usePage()
const { recordSgaGoal } = useRecordSgaGoal()
const isUserLoggedIn          = computed(() => !!(sharedProps.auth?.user?.id))
const isGoldUser              = computed(() => !!(sharedProps.auth?.is_gold))
const routePrefix             = computed(() => sharedProps.routePrefix || 'www')
const requiresAgeVerification = computed(() => !!(sharedProps.requiresAgeVerification))
const userBalance             = computed(() => Number(sharedProps.auth?.user?.wallet_balance) || 0)

// ─── Constants ────────────────────────────────────────────────────────────────
const DRAFT_COOKIE   = 'casting_draft'
const SEVEN_DAYS_MS  = 7 * 24 * 60 * 60 * 1000
const MAX_CAST       = 4
const VALID_FORMATS  = ['Standard', 'VR', 'Vertical', 'Mixed Reality']
const FORMAT_TAG_MAP = { 'Standard': 'Cinematic', 'VR': 'VR', 'Vertical': 'Vertical', 'Mixed Reality': 'Mixed Reality' }
const SETTING_TAG_MAP = {
    bedroom: 'Bedroom', office: 'Office', pool: 'Outdoor', hotel: 'Hotel',
    gym: 'Gym', kitchen: 'Kitchen', living: 'Living Room', classroom: 'Classroom', massage: 'Massage'
}

const successSteps = [
    'Your request enters a review queue where our team will assess feasibility and next steps.',
    'The more community support your casting gets, the stronger the signal to make it happen.',
    'Stay tuned — we\'ll keep you updated as your casting progresses.',
    'All submissions have 30 days from approval to meet their support goal. Submissions that do not meet the goal will be closed.',
]

// ─── Draft persistence (cookie, shared across subdomains) ─────────────────────
function domainPart() {
    if (typeof window !== 'undefined' && window.location.hostname.includes('naughtyamerica.com'))
        return '; domain=.naughtyamerica.com'
    return ''
}

function saveDraft() {
    const expires = new Date(Date.now() + SEVEN_DAYS_MS).toUTCString()
    const data = {
        selectedCast:   selectedCast.value,
        customCast:     customCast.value,
        format:         format.value,
        settings:       settings.value,
        title:          title.value,
        setup:          setup.value,
        directorsNotes: directorsNotes.value,
        tags:           selectedTags.value,
        agreedToTerms:  agreedToTerms.value,
        currentStep:    currentStep.value,
    }
    document.cookie = `${DRAFT_COOKIE}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires}; path=/${domainPart()}; SameSite=Lax`
}

function loadDraft() {
    try {
        const match = document.cookie.match(new RegExp('(?:^|; )' + DRAFT_COOKIE + '=([^;]*)'))
        if (!match) return null
        const parsed = JSON.parse(decodeURIComponent(match[1]))
        if (parsed.format && !VALID_FORMATS.includes(parsed.format)) parsed.format = null
        return parsed
    } catch (e) { return null }
}

const initialState = loadDraft()

// ─── Form state ───────────────────────────────────────────────────────────────
const currentStep    = ref(initialState?.currentStep   ?? 0)
const direction      = ref(1)
const selectedCast   = ref(initialState?.selectedCast  ?? [])
const customCast     = ref(initialState?.customCast    ?? [])
const format         = ref(initialState?.format        ?? 'Standard')
const settings       = ref(initialState?.settings      ?? [])
const title          = ref(initialState?.title         ?? '')
const setup          = ref(initialState?.setup         ?? '')
const directorsNotes = ref(initialState?.directorsNotes ?? '')
const selectedTags   = ref(initialState?.tags          ?? [])
const agreedToTerms  = ref(initialState?.agreedToTerms ?? false)
const isProducerJoinModalOpen = ref(false)
const isUpgradeModalOpen = ref(false)
const isBuyStarsModalOpen = ref(false)
const inputFocused       = ref(false)

const shouldPromptForUpgrade = computed(() => sharedProps.auth?.is_trial && !sharedProps.auth?.is_free_trial)

const castPerformer = computed(() => {
    if (selectedCast.value.length > 0)
        return performers.find(p => p.name === selectedCast.value[0]) ?? null
    return null
})

// ─── Keyboard handling ─────────────────────────────────────────────────────────
function onKeyDown(e) {
    if (e.key === 'Escape' && !isProducerJoinModalOpen.value) {
        e.preventDefault()
        handleBack()
        return
    }
    const target   = e.target
    const isTyping = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && currentStep.value === 5 && canAdvance.value) {
        e.preventDefault(); handleSubmit(); return
    }
    if (e.key === 'Enter' && !isTyping && !e.metaKey && !e.ctrlKey && !e.shiftKey && currentStep.value < 5 && canAdvance.value) {
        e.preventDefault(); nextStep()
    }
}
// ─── Source from prop ─────────────────────────────────────────────────────
const joinModalSource = computed(() => props.source)

onMounted(() => {
    trackFathomEvent('Casting Wizard V3 Page Viewed')
    trackEvent('Casting Wizard Page Viewed', {
        version: 3
    })

    window.addEventListener('keydown', onKeyDown)
})
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))

// ─── Draft auto-save ──────────────────────────────────────────────────────────
watch(
    [selectedCast, customCast, format, settings, title, setup, directorsNotes, selectedTags, agreedToTerms, currentStep],
    saveDraft,
    { deep: true }
)

// ─── Navigation ───────────────────────────────────────────────────────────────
const canAdvance = computed(() => {
    switch (currentStep.value) {
        case 0: return selectedCast.value.length > 0 || customCast.value.length > 0
        case 1: return format.value !== null
        case 2: return true
        case 3: return title.value.trim().length > 0
        case 4: return setup.value.trim().length > 0
        case 5: return agreedToTerms.value
        default: return false
    }
})

const transitionName = computed(() => direction.value > 0 ? 'step-forward' : 'step-back')

function nextStep() {
    if (currentStep.value < 5) {
        direction.value = 1
        currentStep.value++
        trackFathomEvent(`Casting Wizard V3 Step ${currentStep.value + 1} Reached`)
        trackEvent(`Casting Wizard Step ${currentStep.value + 1} Reached`, {
            version: 3
        })
    }
}
function prevStep() {
    if (currentStep.value > 0) {
        direction.value = -1
        currentStep.value--
    }
}
function jumpToStep(i) {
    if (i === currentStep.value) return
    direction.value = i > currentStep.value ? 1 : -1
    currentStep.value = i
    // trackFathomEvent(`Casting Wizard V3 Step ${currentStep.value} Reached`)
    // trackEvent(`Casting Wizard V3 Step ${currentStep.value} Reached`)
    // trackFeatureEvent(`Casting Wizard Step ${currentStep.value} Reached`, 'pitch-wizard')
}
function handleBack() {
    if (currentStep.value > 0) prevStep()
    else router.visit(route(routePrefix.value + '.reels.tag', { view: 'castings' }))
}

// ─── Suggested tags ────────────────────────────────────────────────────────────
const suggestedTags = computed(() => {
    const suggestions = []
    if (format.value && FORMAT_TAG_MAP[format.value]) suggestions.push(FORMAT_TAG_MAP[format.value])
    for (const s of settings.value) {
        if (SETTING_TAG_MAP[s]) suggestions.push(SETTING_TAG_MAP[s])
    }
    return suggestions.slice(0, 3).filter(t => !selectedTags.value.includes(t))
})

// ─── Cast helpers ──────────────────────────────────────────────────────────────
function toggleCast(name) {
    if (selectedCast.value.includes(name))
        selectedCast.value = selectedCast.value.filter(n => n !== name)
    else if (selectedCast.value.length + customCast.value.length < MAX_CAST)
        selectedCast.value = [...selectedCast.value, name]
}
function addCustomCast(name) {
    if (!customCast.value.includes(name)) customCast.value = [...customCast.value, name]
}
function removeCustomCast(name) {
    customCast.value = customCast.value.filter(n => n !== name)
}

// ─── Submit ────────────────────────────────────────────────────────────────────
const loading = ref(false)
const submitted = ref(false)
const submittedSlug = ref(null)
const errorMessage = ref('')
const similarCasting = ref(null)

function clearDraft() {
    document.cookie = `${DRAFT_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart()}`
}

const handleUpgradeModalClose = () => {
    isUpgradeModalOpen.value = false
    trackFathomEvent('Create Casting V3 Upgrade Modal Closed')
    trackEvent('Create Casting Upgrade Modal Closed', {
        version: 3
    })
}

const handleBuyStarPack = (pack) => {
    openStarPackPurchaseUrl(pack, 'create_casting')
}

async function handleSubmit(force = false) {
    if (!agreedToTerms.value) return

    if (!isUserLoggedIn.value) {
        isProducerJoinModalOpen.value = true
        return
    }

    if (shouldPromptForUpgrade.value) {
        isUpgradeModalOpen.value = true
        trackFathomEvent('Create Casting V3 Upgrade Modal Opened')
        trackEvent('Create Casting Upgrade Modal Opened', {
            version: 3
        })
        return
    }

    loading.value = true
    errorMessage.value = ''
    similarCasting.value = null

    const formData = {
        title:                 title.value || null,
        performers:            selectedCast.value.map(name => performers.find(p => p.name === name)?.id).filter(Boolean),
        performer_description: customCast.value.length > 0 ? customCast.value.join(', ') : null,
        format:                format.value || null,
        tags:                  selectedTags.value.map(name => tags.find(t => t.name === name)).filter(Boolean),
        description:           setup.value || null,
        setting:               settings.value,
        director_notes:        directorsNotes.value || null,
        source:                'CreateV3',
        force,
    }

    try {
        const { data } = await axios.post(route('members2.reels.casting.store'), formData)

        clearDraft()
        submittedSlug.value = data.casting_slug ?? null
        submitted.value = true

        recordSgaGoal('create_pitch', { casting_id: data.casting_id })

        // Track analytics
        trackFathomEvent('Casting Wizard V3 Pitch Submitted')
        trackEvent('Casting Wizard Pitch Submitted', {
            ...formData,
            casting_id: data.casting_id,
            version: 3
        })
    } catch (e) {
        if (e.response?.status === 409 && e.response?.data?.similar) {
            similarCasting.value = e.response.data.existing
        } else if (e.response?.status === 422 && e.response?.data?.errors) {
            const errors = e.response.data.errors
            errorMessage.value = Object.values(errors).flat()[0] || 'Please check your inputs and try again.'
        } else {
            if (e.response?.data?.message && e.response.data.message.toLowerCase().includes('insufficient balance')) {
                setTimeout(() => isBuyStarsModalOpen.value = true, 500)
            }

            errorMessage.value = e.response?.data?.message || 'Something went wrong. Please try again.'
        }
    } finally {
        loading.value = false
    }
}

// ─── Step components + props/events ───────────────────────────────────────────
const STEP_COMPONENTS = [
    CastStepVue, FormatStepVue, SettingStepVue,
    TitleStepVue, SynopsisStepVue, LaunchStepVue
]

const currentStepComponent = computed(() => STEP_COMPONENTS[currentStep.value])

const stepProps = computed(() => {
    switch (currentStep.value) {
        case 0: return { selectedCast: selectedCast.value, customCast: customCast.value, performers, popularPerformers }
        case 1: return { format: format.value, selectedCast: selectedCast.value, customCast: customCast.value }
        case 2: return { settings: settings.value }
        case 3: return { title: title.value, settings: settings.value }
        case 4: return {
            setup:          setup.value,
            directorsNotes: directorsNotes.value,
            tags:           selectedTags.value,
            suggestedTags:  suggestedTags.value,
            selectedCast:   selectedCast.value,
            customCast:     customCast.value,
            settings:       settings.value,
            allTags:        tags.map(t => t.name),
        }
        case 5: return {
            data: {
                selectedCast:   selectedCast.value,
                customCast:     customCast.value,
                format:         format.value,
                settings:       settings.value,
                title:          title.value,
                setup:          setup.value,
                directorsNotes: directorsNotes.value,
                tags:           selectedTags.value,
                agreedToTerms:  agreedToTerms.value,
            },
            performers,
            castingStats,
            allTags: tags.map(t => t.name),
            loading: loading.value,
            similarCasting: similarCasting.value,
            errorMessage: errorMessage.value,
            userPitchesCount
        }
        default: return {}
    }
})

const stepEvents = computed(() => {
    switch (currentStep.value) {
        case 0: return {
            toggleCast, addCustomCast, removeCustomCast,
            next: nextStep,
            'update:inputFocused': v => { inputFocused.value = v },
        }
        case 1: return {
            'update:format': v => { format.value = v },
            next: nextStep,
        }
        case 2: return {
            toggleSetting: id => {
                if (settings.value.includes(id)) settings.value = settings.value.filter(s => s !== id)
                else settings.value = [...settings.value, id]
            },
            addCustomSetting: val => {
                if (!settings.value.includes(val)) settings.value = [...settings.value, val]
            },
            removeSetting: id => { settings.value = settings.value.filter(s => s !== id) },
            next: nextStep,
            'update:inputFocused': v => { inputFocused.value = v },
        }
        case 3: return {
            'update:title': v => { title.value = v },
            next: nextStep,
            'update:inputFocused': v => { inputFocused.value = v },
        }
        case 4: return {
            'update:setup':          v => { setup.value = v },
            'update:directorsNotes': v => { directorsNotes.value = v },
            toggleTag: t => {
                if (selectedTags.value.includes(t)) selectedTags.value = selectedTags.value.filter(x => x !== t)
                else selectedTags.value = [...selectedTags.value, t]
            },
            next: nextStep,
            'update:inputFocused': v => { inputFocused.value = v },
        }
        case 5: return {
            'update:agreedToTerms': v => { agreedToTerms.value = v },
            editStep: jumpToStep,
            submit: (force = false) => handleSubmit(force),
            dismissSimilar: () => { similarCasting.value = null },
            'update:title': v => { title.value = v },
            toggleCast, addCustomCast, removeCustomCast,
            'update:format': v => { format.value = v },
            toggleSetting: id => {
                if (settings.value.includes(id)) settings.value = settings.value.filter(s => s !== id)
                else settings.value = [...settings.value, id]
            },
            addCustomSetting: val => {
                if (!settings.value.includes(val)) settings.value = [...settings.value, val]
            },
            removeSetting: id => { settings.value = settings.value.filter(s => s !== id) },
            toggleTag: t => {
                if (selectedTags.value.includes(t)) selectedTags.value = selectedTags.value.filter(x => x !== t)
                else selectedTags.value = [...selectedTags.value, t]
            },
        }
        default: return {}
    }
})
</script>

<style scoped>
/* ─── Root ──────────────────────────────────────────────────────────────────── */
.page-root {
    /* fill the viewport below the sticky site nav (56px) */
    height: 100dvh;
    display: flex;
    flex-direction: column;
    background: #0A0A0A;
    font-family: 'Inter', sans-serif;
    -webkit-font-smoothing: antialiased;
    overflow: hidden;
    padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ─── Header — always visible, never scrolls ─────────────────────────────────── */
.page-header {
    flex-shrink: 0;
    background: #0A0A0A;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-bottom: 1px solid rgba(255,255,255,0.06);
}
@media (min-width: 768px) {
    .page-header {
        width: 100%;
        max-width: 672px;
        margin: 16px auto 0;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 16px 16px 0 0;
    }
}

/* ─── Body — takes remaining height, step components scroll inside ────────────── */
.page-body {
    width: 100%;
    max-width: 672px;
    margin: 0 auto;
    flex: 1;
    min-height: 0; /* required for flex children to shrink correctly */
    position: relative;
    overflow: hidden;
}
@media (min-width: 768px) {
    .page-body {
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-top: none;
        border-radius: 0 0 16px 16px;
        padding: 0 12px 16px;
    }
}

/* ─── Progress bar ──────────────────────────────────────────────────────────── */
:deep(.progress-bar-wrap) {
    width: 100%;
    max-width: 672px;
    margin: 0 auto;
    padding: 12px 16px;
}
:deep(.progress-bar-track) {
    display: flex;
    gap: 6px;
    margin-bottom: 8px;
}
:deep(.progress-bar-segment) {
    height: 3px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    overflow: hidden;
}
:deep(.progress-bar-fill) {
    height: 100%;
    background: white;
    transition: width 0.4s ease;
}
:deep(.progress-bar-label) {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 600;
}
:deep(.progress-bar-step-name) {
    color: rgba(255, 255, 255, 0.5);
    font-weight: 400;
}

/* ─── Transitions ───────────────────────────────────────────────────────────── */

.step-forward-enter-active,
.step-forward-leave-active { transition: transform 0.2s ease-out, opacity 0.2s; position: absolute; width: 100%; height: 100%; }
.step-forward-enter-from   { transform: translateX(100%); opacity: 0; }
.step-forward-leave-to     { transform: translateX(-100%); opacity: 0; }

.step-back-enter-active,
.step-back-leave-active { transition: transform 0.2s ease-out, opacity 0.2s; position: absolute; width: 100%; height: 100%; }
.step-back-enter-from   { transform: translateX(-100%); opacity: 0; }
.step-back-leave-to     { transform: translateX(100%); opacity: 0; }

/* ─── Success Panel ─────────────────────────────────────────────────────────── */
.success-panel {
    padding: 40px 24px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    overflow-y: auto;
    height: 100%;
}
.success-icon-stack { position: relative; }
.success-icon-wrap {
    width: 80px;
    height: 80px;
    border-radius: 24px;
    background: linear-gradient(135deg, rgba(124,58,237,0.4), rgba(147,51,234,0.2));
    border: 1px solid rgba(139,92,246,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
}
.success-icon-glow {
    position: absolute;
    inset: -8px;
    border-radius: 24px;
    background: rgba(139,92,246,0.2);
    filter: blur(20px);
    z-index: -1;
    animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}
.success-title {
    font-size: 24px;
    font-weight: 900;
    color: white;
    margin: 0 0 8px;
}
.success-sub {
    color: rgba(255,255,255,0.55);
    font-size: 14px;
    line-height: 1.6;
    max-width: 380px;
    margin: 0 auto;
}
.success-steps {
    width: 100%;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 16px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: left;
}
.success-step-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}
.success-num {
    width: 24px;
    height: 24px;
    border-radius: 9999px;
    background: rgba(124,58,237,0.2);
    border: 1px solid rgba(139,92,246,0.3);
    color: #c4b5fd;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
}
.success-step-text {
    color: rgba(255,255,255,0.6);
    font-size: 12px;
    line-height: 1.6;
    margin: 0;
}
.primary-cta {
    width: 100%;
    padding: 16px;
    font-size: 14px;
    font-weight: 700;
    font-family: inherit;
    border-radius: 16px;
    border: 0;
    background: linear-gradient(to right, #7c3aed, #c026d3);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 20px 40px -10px rgba(124,58,237,0.4);
    transition: all 0.2s;
}
.primary-cta:hover {
    background: linear-gradient(to right, #8b5cf6, #d946ef);
}
.primary-cta:active {
    transform: scale(0.98);
}

/* ─── Utilities ─────────────────────────────────────────────────────────────── */
:deep(input), :deep(textarea), :deep(select) { font-size: 16px !important; }
:deep(.hide-scrollbar)::-webkit-scrollbar { display: none; }
:deep(.hide-scrollbar) { -ms-overflow-style: none; scrollbar-width: none; }
:deep(.pb-safe) { padding-bottom: env(safe-area-inset-bottom, 0px); }
:deep(.pt-safe) { padding-top: env(safe-area-inset-top, 0px); }
</style>

<style>
/* Zero out the global layout bottom padding for this full-screen wizard page */
#masterlayout:has(.page-root) { padding-bottom: 0 !important; }
</style>
